import React, { useState, useEffect } from "react";
import axios from "axios";
import { Card, CardContent, CardHeader, CardTitle } from "./ui/card";
import { Badge } from "./ui/badge";
import { Eye, UserX, Lock, AlertCircle } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const FEATURE_LABELS = {
  jobs: "Balcão de Emprego",
  calendar: "Calendário",
  evaluations: "Avaliações",
  performance: "Desempenho",
  marketplace: "Marketplace",
  market: "Mercado",
  community: "Comunidade",
  plans: "Planos",
  reports: "Relatórios",
  professionals: "Profissionais",
  messages: "Mensagens",
};

const ENGAGEMENT = {
  diario: { label: "🟢 Diário", cls: "bg-green-600 text-white" },
  ativo: { label: "🟢 Ativo", cls: "bg-green-100 text-green-800" },
  esporadico: { label: "🟡 Esporádico", cls: "bg-yellow-100 text-yellow-800" },
  um_dia: { label: "🟠 Usou 1 dia", cls: "bg-orange-100 text-orange-800" },
  sumido: { label: "🔴 Sumido", cls: "bg-red-100 text-red-800" },
  nunca_entrou: { label: "⚪ Nunca entrou", cls: "bg-gray-100 text-gray-600" },
};

const SummaryCard = ({ label, value, cls }) => (
  <div className={`rounded-lg p-3 text-center ${cls}`}>
    <p className="text-2xl font-bold">{value}</p>
    <p className="text-[11px] font-medium">{label}</p>
  </div>
);

const AdminUsagePanel = () => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    axios
      .get(`${API}/admin/usage-stats`)
      .then((res) => setData(res.data))
      .catch(() => setData(null))
      .finally(() => setLoading(false));
  }, []);

  if (loading) return <p className="text-sm text-muted-foreground py-4">Carregando uso da plataforma...</p>;
  if (!data) return null;

  const { summary, per_user, screens_ranking, screens_never_accessed, blocked_attempts, abandonment } = data;

  return (
    <div className="space-y-6" data-testid="admin-usage-panel">
      <h2 className="text-xl font-bold flex items-center gap-2">
        <Eye className="w-5 h-5" />
        Uso da Plataforma
      </h2>

      {/* Resumo de engajamento */}
      <div className="grid grid-cols-3 sm:grid-cols-6 gap-2" data-testid="usage-summary-cards">
        <SummaryCard label="Diários" value={summary.diario} cls="bg-green-600 text-white" />
        <SummaryCard label="Ativos (7d)" value={summary.ativo} cls="bg-green-100 text-green-800" />
        <SummaryCard label="Esporádicos" value={summary.esporadico} cls="bg-yellow-100 text-yellow-800" />
        <SummaryCard label="Usaram 1 dia" value={summary.um_dia} cls="bg-orange-100 text-orange-800" />
        <SummaryCard label="Sumidos (30d+)" value={summary.sumido} cls="bg-red-100 text-red-800" />
        <SummaryCard label="Nunca entraram" value={summary.nunca_entrou} cls="bg-gray-100 text-gray-600" />
      </div>

      {/* Por usuário */}
      <Card>
        <CardHeader className="pb-3">
          <CardTitle className="text-base">Engajamento por Usuário</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="overflow-x-auto">
            <table className="w-full text-sm" data-testid="usage-users-table">
              <thead>
                <tr className="text-left text-xs text-muted-foreground border-b">
                  <th className="py-2 pr-2">Usuário</th>
                  <th className="py-2 pr-2">Situação</th>
                  <th className="py-2 pr-2">Último acesso</th>
                  <th className="py-2 pr-2">Dias ativos (30d)</th>
                  <th className="py-2">Telas mais usadas</th>
                </tr>
              </thead>
              <tbody>
                {per_user.map((u, i) => {
                  const eng = ENGAGEMENT[u.engagement] || ENGAGEMENT.nunca_entrou;
                  return (
                    <tr key={i} className="border-b last:border-0">
                      <td className="py-2 pr-2">
                        <p className="font-medium truncate max-w-[160px]">{u.name || "Sem nome"}</p>
                        <p className="text-[11px] text-muted-foreground truncate max-w-[160px]">{u.email}</p>
                      </td>
                      <td className="py-2 pr-2">
                        <Badge className={`${eng.cls} text-[10px] whitespace-nowrap`}>{eng.label}</Badge>
                      </td>
                      <td className="py-2 pr-2 whitespace-nowrap text-xs">
                        {u.last_activity ? `${new Date(u.last_activity).toLocaleDateString("pt-BR")} (${u.days_since}d)` : "—"}
                      </td>
                      <td className="py-2 pr-2 text-center">{u.active_days_30d}</td>
                      <td className="py-2 text-xs">
                        {u.top_features.length > 0
                          ? u.top_features.map((f) => FEATURE_LABELS[f] || f).join(", ")
                          : "—"}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </CardContent>
      </Card>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        {/* Ranking de telas */}
        <Card>
          <CardHeader className="pb-3">
            <CardTitle className="text-base">Telas Mais Acessadas</CardTitle>
          </CardHeader>
          <CardContent>
            {screens_ranking.length === 0 ? (
              <p className="text-sm text-muted-foreground" data-testid="no-screen-data">
                Os acessos começam a ser registrados a partir de agora — os dados aparecerão aqui conforme os usuários navegarem.
              </p>
            ) : (
              <div className="space-y-2" data-testid="screens-ranking">
                {screens_ranking.map((s, i) => (
                  <div key={s.feature} className="flex items-center justify-between py-1 border-b last:border-0">
                    <span className="text-sm">
                      <span className="text-muted-foreground mr-2">{i + 1}º</span>
                      {FEATURE_LABELS[s.feature] || s.feature}
                    </span>
                    <span className="text-xs text-muted-foreground">
                      {s.views_30d} acessos (30d) · {s.unique_users_30d} usuários
                    </span>
                  </div>
                ))}
              </div>
            )}
            {screens_never_accessed.length > 0 && (
              <div className="mt-4 pt-3 border-t">
                <p className="text-xs font-medium text-red-700 mb-2 flex items-center gap-1">
                  <AlertCircle className="w-3 h-3" /> Telas sem nenhum acesso registrado:
                </p>
                <div className="flex flex-wrap gap-1">
                  {screens_never_accessed.map((f) => (
                    <Badge key={f} variant="outline" className="text-[10px] text-red-600 border-red-300">
                      {FEATURE_LABELS[f] || f}
                    </Badge>
                  ))}
                </div>
              </div>
            )}
          </CardContent>
        </Card>

        {/* Bloqueios + Abandono */}
        <Card>
          <CardHeader className="pb-3">
            <CardTitle className="text-base flex items-center gap-2">
              <Lock className="w-4 h-4" /> Travas e Pontos de Abandono
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4 text-sm">
            <div data-testid="blocked-attempts">
              <p className="text-xs font-medium text-muted-foreground mb-1">Tentativas bloqueadas pelo plano (30d):</p>
              {blocked_attempts.length === 0 ? (
                <p className="text-xs text-muted-foreground">Nenhuma registrada ainda.</p>
              ) : (
                blocked_attempts.map((b) => (
                  <div key={b.feature} className="flex justify-between py-1">
                    <span>{FEATURE_LABELS[b.feature] || b.feature}</span>
                    <Badge className="bg-amber-100 text-amber-800">{b.count_30d}x</Badge>
                  </div>
                ))
              )}
            </div>
            <div className="pt-2 border-t" data-testid="abandonment-section">
              <p className="text-xs font-medium text-muted-foreground mb-2 flex items-center gap-1">
                <UserX className="w-3 h-3" /> Pontos de abandono:
              </p>
              <div className="space-y-2 text-xs">
                <div>
                  <span className="font-medium text-orange-700">Proprietários sem propriedade cadastrada ({abandonment.employers_no_property.length}):</span>
                  <p className="text-muted-foreground break-words">{abandonment.employers_no_property.join(", ") || "nenhum"}</p>
                </div>
                <div>
                  <span className="font-medium text-orange-700">Com propriedade mas sem registros de sangria ({abandonment.employers_no_records.length}):</span>
                  <p className="text-muted-foreground break-words">{abandonment.employers_no_records.join(", ") || "nenhum"}</p>
                </div>
                <div>
                  <span className="font-medium text-orange-700">Entraram 1 vez e nunca voltaram ({abandonment.never_returned.length}):</span>
                  <p className="text-muted-foreground break-words">{abandonment.never_returned.join(", ") || "nenhum"}</p>
                </div>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default AdminUsagePanel;
